import { useState } from 'react';
import { ChevronDown, FileText } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { AccountSummaryCard } from '../components/AccountSummaryCard';
import { TransactionFilters } from '../components/TransactionFilters';
import { CommissionTransactionList } from '../components/CommissionTransactionList';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useEstimatorAccount } from '../hooks/useEstimatorAccount';
import { useCommissionTransactions } from '../hooks/useCommissionTransactions';
import { useEstimatorReport } from '../hooks/useEstimatorReport';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { TransactionFilters as FilterType } from '../types/commission';

/**
 * My Account page — read-only view of the current user's estimator account.
 * Shows commissions earned, draws, running balance, and transaction history.
 */
export function MyAccountPage() {
  usePageTitle('My Account');
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [filters, setFilters] = useState<FilterType>({});

  const { downloadPdf, downloadExcel, openPdf, isLoading: reportLoading } =
    useEstimatorReport(userId);

  const { data: account, isLoading: accountLoading } =
    useEstimatorAccount(userId);

  const { data: transactions, isLoading: transactionsLoading } =
    useCommissionTransactions(userId, filters);

  if (!userId) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12 text-gray-500">
          Unable to load account information.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">My Account</h1>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              disabled={reportLoading}
            >
              <FileText className="h-4 w-4 mr-1" />
              {reportLoading ? 'Loading...' : 'Report'}
              <ChevronDown className="h-3 w-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white">
            <DropdownMenuItem
              onClick={() => openPdf({ startDate: filters.startDate, endDate: filters.endDate })}
              className="cursor-pointer"
            >
              View PDF
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => downloadPdf({ startDate: filters.startDate, endDate: filters.endDate })}
              className="cursor-pointer"
            >
              Download PDF
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => downloadExcel({ startDate: filters.startDate, endDate: filters.endDate })}
              className="cursor-pointer"
            >
              Download Excel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AccountSummaryCard
        account={account}
        isLoading={accountLoading}
      />

      <TransactionFilters
        filters={filters}
        onChange={setFilters}
      />

      <CommissionTransactionList
        transactionList={transactions}
        isLoading={transactionsLoading}
      />
    </div>
  );
}
